import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { academicService } from '../../services/academicService';
import { assignmentService } from '../../services/assignmentService';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, 
  Users, 
  BookOpen, 
  FileText, 
  GraduationCap,
  Building,
  Edit,
  Eye
} from 'lucide-react';
import DataTable from '../../components/ui/data-table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const ClassDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  useEffect(() => {
    if (id) {
      fetchClass();
    }
  }, [id]);

  const fetchClass = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await academicService.getClass(id);
      const data = response.data?.data?.class || response.data?.data || response.data;
      setClassData(data);
      
      // Fetch students and assignments in parallel
      await Promise.all([
        fetchStudents(id),
        fetchAssignments(id)
      ]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load class');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async (classId) => {
    try {
      setLoadingStudents(true);
      const response = await academicService.getClassStudents(classId);
      const studentsData = response.data?.data?.students || response.data?.data || [];
      setStudents(studentsData);
    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchAssignments = async (classId) => {
    try {
      setLoadingAssignments(true);
      const response = await assignmentService.getAssignments({ classId, teacherId: user?.id });
      const assignmentsData = response.data?.data?.assignments || response.data?.data || [];
      setAssignments(assignmentsData);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    } finally {
      setLoadingAssignments(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-red-600 text-sm">{error}</p>
            <Button className="mt-3" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-gray-600">Class not found</p>
            <Button className="mt-3" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const enrollments = classData.enrollments || [];
  const classSubjects = classData.classSubjects || [];
  const classTeacher = classData.classTeacher;

  const studentColumns = [
    { id: 'name', header: 'Name', accessorKey: 'name', 
      cell: ({ row }) => {
        const student = row.original || row;
        if (!student) return 'N/A';
        return `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.email || 'N/A';
      }
    },
    { id: 'email', header: 'Email', accessorKey: 'email',
      cell: ({ row }) => {
        const student = row.original || row;
        return student?.email || 'N/A';
      }
    },
    { id: 'admissionNumber', header: 'Admission Number', 
      cell: ({ row }) => {
        const student = row.original || row;
        if (!student) return 'N/A';
        return student.profile?.admissionNumber || student.admissionNumber || 'N/A';
      }
    },
    { id: 'status', header: 'Status',
      cell: ({ row }) => {
        const student = row.original || row;
        if (!student) return <Badge className="bg-gray-100 text-gray-800">N/A</Badge>;
        const isActive = student.isActive !== false;
        return (
          <Badge className={isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        );
      }
    }
  ];

  const assignmentColumns = [
    { 
      id: 'title', 
      header: 'Title', 
      accessorKey: 'title', 
      sortable: true,
      cell: ({ row }) => {
        const assignment = row.original || row;
        return assignment?.title || 'N/A';
      }
    },
    { 
      id: 'subject', 
      header: 'Subject', 
      cell: ({ row }) => {
        const assignment = row.original || row;
        if (!assignment) return 'N/A';
        return assignment.subject?.name || assignment.subjectName || 'N/A';
      }
    },
    { 
      id: 'dueDate', 
      header: 'Due Date', 
      cell: ({ row }) => {
        const assignment = row.original || row;
        if (!assignment?.dueDate) return 'N/A';
        return new Date(assignment.dueDate).toLocaleDateString();
      },
      sortable: true
    },
    { 
      id: 'status', 
      header: 'Status',
      cell: ({ row }) => {
        const assignment = row.original || row;
        if (!assignment) return <Badge className="bg-gray-100 text-gray-800">N/A</Badge>;
        const status = assignment.status || 'draft';
        const statusColors = {
          'published': 'bg-green-100 text-green-800',
          'draft': 'bg-gray-100 text-gray-800',
          'closed': 'bg-red-100 text-red-800',
          'graded': 'bg-blue-100 text-blue-800'
        };
        return (
          <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
            {status}
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const assignment = row.original || row;
        if (!assignment?.id) return null;
        return (
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate(`/dashboard/assignments/${assignment.id}`)}
            >
              <Eye className="w-4 h-4" />
            </Button>
            {(user?.role === 'admin' || user?.role === 'teacher' || user?.role === 'staff') && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => navigate(`/dashboard/assignments/${assignment.id}/grade`)}
              >
                <FileText className="w-4 h-4" />
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Class Details</h1>
            <p className="text-gray-600 mt-1">View class information, students, and assignments</p>
          </div>
        </div>
        {(user?.role === 'admin' || user?.role === 'staff' || user?.role === 'teacher') && (
          <Button
            onClick={() => navigate(`/dashboard/assignments/create?classId=${id}`)}
            className="flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Create Assignment
          </Button>
        )}
      </div>

      {/* Class Information Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-6">
            <div className="p-4 bg-indigo-100 rounded-lg">
              <Building className="w-12 h-12 text-indigo-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-900">
                  {classData.name}
                </h2>
                <Badge variant="outline" className="text-sm">
                  {classData.level || 'N/A'}
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>
                  <span className="font-medium">Academic Year:</span> {classData.academicYear || 'N/A'}
                </span>
                <span>
                  <span className="font-medium">Term:</span> {classData.term || 'N/A'}
                </span>
                <span>
                  <span className="font-medium">Capacity:</span> {classData.capacity || 0} students
                </span>
                <span>
                  <span className="font-medium">Enrolled:</span> {enrollments.length} students
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {classTeacher && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-gray-600" />
                <span className="font-medium">Class Teacher:</span>
                <span>{classTeacher.firstName} {classTeacher.lastName}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for Students, Subjects, and Assignments */}
      <Tabs defaultValue="students" className="space-y-4">
        <TabsList>
          <TabsTrigger value="students">
            <Users className="w-4 h-4 mr-2" />
            Students ({students.length || enrollments.length})
          </TabsTrigger>
          <TabsTrigger value="subjects">
            <BookOpen className="w-4 h-4 mr-2" />
            Subjects ({classSubjects.length})
          </TabsTrigger>
          <TabsTrigger value="assignments">
            <FileText className="w-4 h-4 mr-2" />
            Assignments ({assignments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="students">
          <Card>
            <CardContent className="p-0">
              {loadingStudents ? (
                <div className="p-6 text-center">
                  <LoadingSpinner />
                </div>
              ) : students.length === 0 && enrollments.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No students enrolled in this class
                </div>
              ) : (
                <DataTable 
                  data={students.length > 0 ? students : enrollments.map(e => e.student || e)} 
                  columns={studentColumns} 
                  searchable 
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subjects">
          <Card>
            <CardContent className="p-0">
              {classSubjects.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No subjects assigned to this class
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teacher</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {classSubjects.map((cs) => {
                        const subject = cs.subject;
                        const teacher = cs.teacher;
                        return (
                          <tr key={cs.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-blue-600" />
                                <span className="font-medium">{subject?.name || 'N/A'}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge variant="outline">{subject?.code || 'N/A'}</Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {teacher ? (
                                <span className="text-sm">
                                  {teacher.firstName} {teacher.lastName}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-sm">Not assigned</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments">
          <Card>
            <CardContent className="p-0">
              {loadingAssignments ? (
                <div className="p-6 text-center">
                  <LoadingSpinner />
                </div>
              ) : assignments.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No assignments for this class
                </div>
              ) : (
                <DataTable data={assignments} columns={assignmentColumns} searchable />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClassDetailPage;

